import { useEffect, useState } from "react";
import { fetchPaintings, fetchSettings } from "../lib/api";
import { BrandBanner } from "./BrandBanner";
import { LetterForm } from "./LetterForm";

export function RightsLine({ className = "", testid = "rights-line", dark = false }) {
  return (
    <a
      data-testid={testid}
      href="/about"
      className={`text-[10px] tracking-[0.18em] transition-colors duration-200 ${dark ? "text-white/70 hover:text-white" : "text-[#6B5A7A]/70 hover:text-[#5B3A85]"} ${className}`}
    >
      © 2026 Priyanka Jha · All rights reserved
    </a>
  );
}

// A short first line (no full stop) is the text's own title; it becomes the heading so nothing is said twice.
const GENERIC = /^(about(\s*(&|and)\s*rights)?|rights|feeling heart|rainbow by the rain)$/i;
function splitTitle(text) {
  const [first, ...rest] = (text || "").trim().split("\n");
  const line = (first || "").trim();
  if (rest.length && line.length <= 48 && !/[.!?]$/.test(line)) return { title: GENERIC.test(line) ? "" : line, body: rest.join("\n").trim() };
  return { title: "", body: (text || "").trim() };
}
// Plain text with paragraph breaks; *words* become italic. No markup is ever injected.
function Paras({ text, className, testid }) {
  return (
    <div data-testid={testid} className="flex flex-col gap-4">
      {text.split(/\n\s*\n/).map((para, i) => (
        <p key={i} className={`whitespace-pre-line ${className}`}>
          {para.split(/(\*[^*\n]+\*)/).map((part, j) => (/^\*[^*]+\*$/.test(part) ? <em key={j}>{part.slice(1, -1)}</em> : part))}
        </p>
      ))}
    </div>
  );
}

export default function AboutPage() {
  const [s, setS] = useState(null);
  const [paintings, setPaintings] = useState([]);
  useEffect(() => {
    fetchSettings().then(setS).catch(() => setS({}));
    fetchPaintings().then(setPaintings).catch(() => {});
  }, []);
  const about = splitTitle(s?.about_text);
  const rights = splitTitle(s?.rights_text);
  return (
    <main data-testid="about-page" className="min-h-screen bg-[radial-gradient(ellipse_at_top,#F3E3F0_0%,#FBF7F2_55%)] py-14 pl-14 pr-6 text-[#2D1E3E] sm:px-24">
      <BrandBanner />
      <div className="mx-auto grid max-w-5xl gap-12 lg:grid-cols-[1.1fr_1fr]">
       <div className="flex flex-col gap-10">
        <a href="/" data-testid="about-back-link" className="self-start font-display text-lg text-[#5B3A85] transition-colors duration-200 hover:text-[#5B3A85]">
          ← back to the gallery
        </a>
        <section className="flex flex-col gap-3">
          <h1 data-testid="about-lockup" className="font-display text-4xl font-light italic sm:text-5xl">{about.title || "About & rights"}</h1>
          <Paras testid="about-text" text={about.body} className="font-display text-xl leading-relaxed text-[#4A3660]" />
        </section>
        <section className="flex flex-col gap-3 border-l border-[#7E57C2]/40 pl-5">
          <h2 data-testid="rights-title" className="font-display text-2xl italic text-[#5B3A85]">{rights.title || "Rights"}</h2>
          <Paras testid="rights-text" text={rights.body} className="font-display text-lg leading-relaxed" />
          {s?.contact_text?.trim() && (
            <p data-testid="contact-text" className="font-display text-lg leading-relaxed text-[#4A3660]">
              {s.contact_text}
            </p>
          )}
        </section>
        <p className="font-display text-sm text-[#5C4A72]">© 2026 Priyanka Jha · All rights reserved</p>
       </div>
       <section id="contact" data-testid="about-letter" className="self-start">
        <LetterForm inline paintings={paintings} />
       </section>
      </div>
    </main>
  );
}
